import { gql, useQuery } from '@apollo/client';
import { currentTripStateIdVar } from '../search/currentTripState';
import { currentHotelFilterSelectionsVar } from '../filter/currentHotelFilters';
import { callSideEffects } from '../../util';
import { CurrencyFormats, DateFormats } from '../../contracts/types';
export const HOTEL_ROOMS_AVAILABILITY = `
    boardOptions {
                boardType
                boardTypeDescription
                requestedRooms {
                    availableRooms {
                        masterRoom {
                            id
                            description
                            title
                            imageUrl
                        }
                        pricingSummary {
                            isSponsored
                            checkInDate
                            checkOutDate
                            numberOfNights
                            departureDate
                            wasPrice
                            wasPriceFormatted
                            wasPricePerNight
                            wasPricePerNightFormatted
                            wasPricePerPerson
                            wasPricePerPersonFormatted
                            boardTypeDescription
                            cityTax
                            cityTaxFormatted
                            refundable {
                                isRefundable
                                refundCutOffDate
                            }
                            cityTaxPerPerson
                            cityTaxPerPersonFormatted
                            pricePerNight
                            pricePerNightFormatted
                            deposit
                            depositFormatted
                            includedExtras
                            hypeMessages {
                                type
                                title
                                description
                                descriptionHeader
                            }
                            monthlyInstalment {
                                isAvailable
                                initialPayment
                                initialPaymentFormatted(format: $currencyFormat)
                                subsequentPayment
                                subsequentPaymentFormatted(format: $currencyFormat)
                                numberOfInstalments
                            }
                            boardType
                            discountTypes
                            teaserPrice {
                                parts {
                                    type
                                    value
                                }
                                formatted
                                value
                            }
                            teaserPricePerPerson {
                                parts {
                                    type
                                    value
                                }
                                formatted
                                value
                            }
                            callout {
                                text
                                themeColor
                            }
                        }
                        roomId
                    }
                }
                bTInclusions
                bTExclusions
            }
            suggestedRooms
`;
const FLIGHT_LEG_DETAILS = gql `
    fragment FlightLegDetails on FlightLeg {
        duration
        departureDateTime {
            date(format: $departureDateFormat)
            time(format: $timeFormat)
        }
        arrivalDateTime {
            date(format: $departureDateFormat)
            time(format: $timeFormat)
        }
        departureAirport {
            airportCode
            title
        }
        arrivalAirport {
            airportCode
            title
        }
        dayDifference
        stops
        totalBagsIncluded
        segments {
            cabinClass
            airline {
                name
                imageName
            }
            departureAirport {
                airportCode
                title
            }
            arrivalAirport {
                airportCode
                title
            }
            flightNumber
            departureDateFormatted
            arrivalDateFormatted

            departureDateTime {
                date(format: $departureDateFormat)
                time(format: $timeFormat)
            }
            arrivalDateTime {
                date(format: $departureDateFormat)
                time(format: $timeFormat)
            }
            duration
            layoverTime
        }
    }
`;
const HOTEL_BASKET_SUMMARY_QUERY = gql `
    ${FLIGHT_LEG_DETAILS}
    query HotelBasketSummaryResults(
        $estabId: Int!
        $tripStateId: ID!
        $filters: HotelFiltersInput
        $currencyFormat: CurrencyFormats
        $dateFormat: DateFormats
        $departureDateFormat: DateFormats
        $timeFormat: DateFormats
    ) {
        hotelBasketSummary(tripStateId: $tripStateId, estabID: $estabId, filters: $filters) {
            tripStateId
            estabID
            checkInDate
            roomsCount
            isNumberOfNightsAdjusted
            numberOfNights
            stopSellMessage {
                message
                title
            }
            roomDetails {
                boardRoomId
                roomDescription
                boardTypeDescription
                boardType
                pricingSummary {
                    isSponsored
                    checkInDate
                    checkOutDate
                    discountTypes
                    deposit
                    numberOfNights
                    includedExtras
                    departureDate
                    boardTypeDescription
                    cityTaxFormatted(format: $currencyFormat)
                    depositFormatted(format: $currencyFormat)
                    hypeMessages(format: $dateFormat) {
                        type
                        title
                        description
                        descriptionHeader
                    }
                    monthlyInstalment {
                        isAvailable
                        initialPayment
                        initialPaymentFormatted(format: $currencyFormat)
                        subsequentPayment
                        subsequentPaymentFormatted(format: $currencyFormat)
                        numberOfInstalments
                    }
                    wasPrice
                    wasPricePerPerson
                    wasPriceFormatted(format: $currencyFormat)
                    wasPricePerPersonFormatted(format: $currencyFormat)
                    callout {
                        text
                        themeColor
                    }
                    teaserPrice(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                    teaserPricePerPerson(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                }
            }
            flightDetails {
                outboundLeg {
                    ...FlightLegDetails
                }
                inboundLeg {
                    ...FlightLegDetails
                }
                flightDetailsId
                flightId
            }
        }
        hotelRoomsAvailability(tripStateId: $tripStateId, estabID: $estabId, filters: $filters) {
            ${HOTEL_ROOMS_AVAILABILITY}
        }
        hotelAvailability(tripStateId: $tripStateId, estabID: $estabId, filters: $filters) {
            pricingSummary {
                price
                callout {
                    text
                    themeColor
                }
            }
            requestedRooms {
                availability {
                    masterRoom {
                        title
                        id
                    }
                    boardOptions {
                        boardOptionId
                        pricingSummary {
                            boardTypeDescription
                            price
                        }
                    }
                }
            }
        }
    }
`;
export function useHotelBasketSummary(estabId, sideEffects, errorSideEffects, skipQuery = false) {
    return useQuery(HOTEL_BASKET_SUMMARY_QUERY, {
        notifyOnNetworkStatusChange: true,
        ssr: false,
        variables: {
            estabId: parseInt(estabId),
            dateFormat: DateFormats.Default,
            currencyFormat: CurrencyFormats.Rounded,
            filters: currentHotelFilterSelectionsVar(),
            tripStateId: currentTripStateIdVar(),
        },
        // TODO, think of a better approach for these later on to avoid making calls to server again
        fetchPolicy: 'cache-and-network',
        skip: !currentTripStateIdVar() || !estabId || skipQuery,
        onCompleted: (completedData) => {
            if (sideEffects) {
                callSideEffects(sideEffects, completedData);
            }
        },
        onError: (err) => {
            callSideEffects(errorSideEffects, err);
        },
    });
}
